import moment from 'moment';

/**
 * Rolling log-window presets for the container-app Logs tab.
 *
 * Kept as a plain module with no React/DOM dependency so the window arithmetic is
 * directly unit-testable — it is the only automated coverage available for the
 * request-side of container-logs-local-time (there is no FE component or e2e tier
 * in this repo; see specs/container-logs-local-time/test-plan.md).
 *
 * Every preset is a ROLLING INSTANT window of an EXACT duration: "Last 3 days"
 * means a rolling 72h ending at the moment the query is built, not a midnight-snapped
 * calendar range. The multi-day presets used to format with a date-only pattern, which
 * snapped both boundaries to midnight and silently excluded log lines the viewer could
 * see on screen once the displayed time crossed a date boundary.
 *
 * Every preset is therefore expressed in `minutes`/`hours`, never `days`: moment's
 * `days` unit is CALENDAR arithmetic applied through the local date field, so a window
 * spanning a DST transition would be 23h or 25h rather than 24h — a DST-variable span.
 * `minutes`/`hours` go through moment's exact `_milliseconds` and are zone-independent.
 */
export const DEFAULT_LOG_RANGE_NAME = 'Last 1 hour';

export const LOG_RANGE_PRESETS = {
  'Last 30 minutes': { amount: 30, unit: 'minutes' },
  'Last 1 hour': { amount: 1, unit: 'hours' },
  'Last 4 hours': { amount: 4, unit: 'hours' },
  'Last 12 hours': { amount: 12, unit: 'hours' },
  'Last 1 day': { amount: 24, unit: 'hours' },
  'Last 3 days': { amount: 72, unit: 'hours' },
  'Last 7 days': { amount: 168, unit: 'hours' },
  'Last 30 days': { amount: 720, unit: 'hours' },
};

/**
 * Resolves a preset key to the pair of absolute instants to send to the API.
 *
 * Returns ISO-8601 with an explicit UTC marker (moment's toISOString), NOT the
 * previous `dd/MM/yyyy [HH:mm:ss]` local-wall-clock string. That old shape carried
 * no zone information at all, so the server had to guess — and guessed using its
 * own local offset. The instant is derived from the viewer's `moment()` now, so the
 * user's local intent is preserved while the value on the wire is unambiguous.
 *
 * @param presetKey  a key of LOG_RANGE_PRESETS; unknown keys fall back to the default
 * @param now        optional reference instant, for tests — defaults to the real now
 */
export const resolveLogRange = (presetKey, now = undefined) => {
  const preset =
    LOG_RANGE_PRESETS[presetKey] ?? LOG_RANGE_PRESETS[DEFAULT_LOG_RANGE_NAME];

  const end = now === undefined ? moment() : moment(now);
  const start = end.clone().subtract(preset.amount, preset.unit);

  return {
    startDate: start.toISOString(),
    endDate: end.toISOString(),
  };
};
